import React, {useDebugValue} from 'react';

import classNames from 'classnames';

import {useSearchFormContext} from '../../../hooks/useSearchFormContext';

import {
    shouldDestinationFieldAutoProgressOnSearchSelected,
    showDestinationInputFieldError,
    showDestinationIcon
} from '../../../utils/destination';

import {SearchIcon} from '../../SearchIcon/SearchIcon';

const classNamespace = 'search-form';
const fieldClassName = `${classNamespace}__field`;
const fieldWrapperClassName = `${classNamespace}__field-wrapper`;
const wrapperDestinationClassName = `${fieldWrapperClassName}--destination`;
const inputFieldClassName = `${classNamespace}__input-field`;
const inputFieldClassNameContrast = `${classNamespace}__input-field-contrast`;
const inputFieldErrorClassName = `${inputFieldClassName}--error`;
const detailsFieldClassName = `${classNamespace}__details-field`;
const detailsInputFieldClassName = `${classNamespace}__details-input-field`;
const detailsinputFieldClassNameContrast = `${classNamespace}__details-input-field-contrast`;
const destinationDetailsInputFieldClassName = `${classNamespace}__destination-details-input-field`;
const destinationDetailsInputFieldClassNameContrast = `${classNamespace}__destination-details-input-field-contrast`;

// eslint-disable-next-line complexity
function useComponent() {
    const {
        systemOptions,
        systemOptions: {
            site,
            baseUrl,
            appPrefix,
            locale
        },
        mediaSettings,
        mediaSettings: {
            isMobile
        },
        abacusStore,
        formOptions,
        formSettings,
        destinationActions: {
            setIsTypeaheadInputFocus,
            setAllDestinationValues
        },
        destinationEvents: {
            onBlur,
            onFocus,
            onMount,
            onSearchSelected
        },
        destinationOptions,
        destinationOptions: {
            filterType,
            googlePlaceApiKey,
            googlePlaceApiUrl,
            hideSelectedFilter,
            highlightMode,
            iconName,
            id,
            matchMode,
            minLength,
            popoverDesktopMaxWidth,
            popoverDesktopMinMarginRight,
            popoverDesktopMinWidth,
            popoverDesktopWidth,
            popoverMaxDisplayCount,
            popoverMaxDisplayCountMobile,
            recentSearchesDisplayMax,
            remotePath,
            retainUserInput,
            showInlineSuggestion,
            showRecentSearches,
            showResultIcons,
            showResultLabels,
            showSpinner,
            showTrendingSuggestions,
            truncateResultTerms,
            usePopoverAnimation,
            useResultsAnimation
        },
        destinationSettings: {
            isTypeaheadInputFocus
        },
        destinationTranslations: {
            clearSearchLabel,
            errorMessageText,
            noResultsFoundMessage,
            placeholderMessage,
            premiseLabel,
            premisesLabel,
            recentSearchesMessage,
            recentlyViewedLabel,
            resultsFoundMessage,
            selectedMessage,
            trendingDestinationsMessage,
            typeAirportLabel,
            typeCityLabel,
            typeLandmarkLabel,
            typeMetroStationLabel,
            typeNeighborhoodLabel,
            typePropertyLabel,
            typeRegionLabel,
            typeTrainStationLabel,
            whereLabel
        },
        latestDestinationValues,
        latestDestinationValues: {
            destination: search
        },
        datesActions: {
            setIsOpen: setDatesIsOpen
        },
        latestDatesValues,
        guestsActions: {
            setIsOpen: setGuestsIsOpen
        }
    } = useSearchFormContext();

    const {
        isColourContrastAndLabels
    } = formOptions;

    const isshowDestinationInputFieldError = showDestinationInputFieldError(mediaSettings, formOptions, formSettings, destinationOptions, latestDestinationValues, abacusStore, systemOptions);

    let chooseClassNames = classNames(fieldClassName, inputFieldClassName, {
        [inputFieldErrorClassName]: showDestinationInputFieldError(mediaSettings, formOptions, formSettings, destinationOptions, latestDestinationValues, abacusStore, systemOptions)
    }, detailsFieldClassName, detailsInputFieldClassName, destinationDetailsInputFieldClassName);

    if (isshowDestinationInputFieldError && isColourContrastAndLabels) {
        chooseClassNames = classNames(fieldClassName, inputFieldClassNameContrast, {
            [inputFieldErrorClassName]: showDestinationInputFieldError(mediaSettings, formOptions, formSettings, destinationOptions, latestDestinationValues, abacusStore, systemOptions)
        }, detailsFieldClassName, detailsinputFieldClassNameContrast, destinationDetailsInputFieldClassName, destinationDetailsInputFieldClassNameContrast);
    } else if (isshowDestinationInputFieldError) {
        chooseClassNames = classNames(fieldClassName, inputFieldClassName, {
            [inputFieldErrorClassName]: showDestinationInputFieldError(mediaSettings, formOptions, formSettings, destinationOptions, latestDestinationValues, abacusStore, systemOptions)
        }, detailsFieldClassName, detailsInputFieldClassName, destinationDetailsInputFieldClassName);
    } else if (isColourContrastAndLabels) {
        chooseClassNames = classNames(fieldClassName, inputFieldClassNameContrast, {
            [inputFieldErrorClassName]: showDestinationInputFieldError(mediaSettings, formOptions, formSettings, destinationOptions, latestDestinationValues, abacusStore, systemOptions)
        }, detailsFieldClassName, detailsinputFieldClassNameContrast, destinationDetailsInputFieldClassName, destinationDetailsInputFieldClassNameContrast);
    }

    const className = chooseClassNames;

    const wrapperClassName = classNames(fieldWrapperClassName, wrapperDestinationClassName);

    const icon = showDestinationIcon(formOptions, destinationOptions) && (
        <SearchIcon name={iconName}/>
    ) || null;

    const messages = {
        clearsearch: clearSearchLabel,
        destinationplaceholdermsg: placeholderMessage,
        destinationselected: selectedMessage,
        noresultsfound: noResultsFoundMessage,
        premise: premiseLabel,
        premises: premisesLabel,
        recentlyviewed: recentlyViewedLabel,
        recentsearches: recentSearchesMessage,
        resultsfound: resultsFoundMessage,
        trendingdestinations: trendingDestinationsMessage,
        typeairport: typeAirportLabel,
        typecity: typeCityLabel,
        typelandmark: typeLandmarkLabel,
        typemetrostation: typeMetroStationLabel,
        typeneighborhood: typeNeighborhoodLabel,
        typeproperty: typePropertyLabel,
        typeregion: typeRegionLabel,
        typetrainstation: typeTrainStationLabel,
        where: whereLabel
    };

    /* istanbul ignore next */
    function handleBlur(...args) {
        onBlur(...args);
    }

    function handleFocus(...args) {
        setIsTypeaheadInputFocus(true);
        setDatesIsOpen(false);
        setGuestsIsOpen(false);

        onFocus(...args);
    }

    function handleMount(...args) {
        onMount(...args);
    }

    function handleSearchSelected(...args) {
        const [
            changedDestinationValues
        ] = args;

        setAllDestinationValues(changedDestinationValues);

        // TODO: Move this function to handleBlur
        setIsTypeaheadInputFocus(false);

        if (shouldDestinationFieldAutoProgressOnSearchSelected(mediaSettings, formOptions, destinationOptions, changedDestinationValues, latestDatesValues)) {
            setTimeout(function delayedDatesOpen() {
                setDatesIsOpen(true);
            }, 300);
        }

        onSearchSelected(...args);
    }

    const result = {
        className,
        wrapperClassName,
        appPrefix,
        baseUrl,
        errorMessageText,
        filterType,
        googlePlaceApiKey,
        googlePlaceApiUrl,
        hideSelectedFilter,
        highlightMode,
        icon,
        id,
        isMobile,
        isTypeaheadInputFocus,
        locale,
        matchMode,
        messages,
        minLength,
        onBlur: handleBlur,
        onFocus: handleFocus,
        onMount: handleMount,
        onSearchSelected: handleSearchSelected,
        popoverDesktopMaxWidth,
        popoverDesktopMinMarginRight,
        popoverDesktopMinWidth,
        popoverDesktopWidth,
        popoverMaxDisplayCount,
        popoverMaxDisplayCountMobile,
        recentSearchesDisplayMax,
        remotePath,
        retainUserInput,
        search,
        showInlineSuggestion,
        showRecentSearches,
        showResultIcons,
        showResultLabels,
        showSpinner,
        showTrendingSuggestions,
        site,
        truncateResultTerms,
        usePopoverAnimation,
        useResultsAnimation
    };

    useDebugValue(result);

    return result;
}

export {useComponent};
